import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  Injector,
  AfterViewInit
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
  NgControl,
  FormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'rms-data-slider',
  templateUrl: './rms-data-slider.component.html',
  styleUrls: ['../rms-styles.scss', './rms-data-slider.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RmsDataSliderComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RmsDataSliderComponent),
      multi: true
    }
  ]
})
export class RmsDataSliderComponent implements ControlValueAccessor, Validator, AfterViewInit {
  @Input() min: number = 0;
  @Input() max: number = 100;
  @Input() step: number = 1;

  @Input() showInput: boolean = true;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() label?: string = undefined;
  @Input() noMargin: boolean = false;

  @Output() valueChange = new EventEmitter<number | null>();

  public ngControl: NgControl | null = null;

  value: number | null = null;
  rawInput: string = '';

  onChange = (_: number | null) => { };
  onTouched = () => { };

  constructor(private injector: Injector) { }

  ngAfterViewInit(): void {
    this.ngControl = this.injector.get(NgControl as any, null as any, {
      self: true,
      optional: true
    } as any);
  }

  get hasLabel(): boolean {
    return this.label !== undefined;
  }

  get invalid(): boolean {
    return !!(
      this.ngControl &&
      this.ngControl.invalid &&
      (this.ngControl.touched || this.ngControl.dirty)
    );
  }

  get errors(): ValidationErrors | null {
    return this.ngControl?.errors ?? null;
  }

  get errorMessage(): string | null {
    if (!this.errors) return null;

    if (this.errors['required']) {
      return 'This field is required.';
    }

    if (this.errors['min']) {
      return `Value must be greater than or equal to ${this.min}.`;
    }

    if (this.errors['max']) {
      return `Value must be less than or equal to ${this.max}.`;
    }

    return 'Invalid value.';
  }

  writeValue(value: any): void {
    if (value === '' || value === undefined) {
      this.value = null;
      this.rawInput = '';
      return;
    }

    const parsed = this.parseValue(value);
    if (parsed === null) {
      this.value = null;
      this.rawInput = '';
      return;
    }

    this.value = this.clampValue(parsed);
    this.rawInput = this.value.toString();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (this.required && (value === null || value === undefined || value === '')) {
      return { required: true };
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return { invalid: true };
    }

    if (numericValue < this.min) {
      return {
        min: {
          min: this.min,
          actual: numericValue
        }
      };
    }

    if (numericValue > this.max) {
      return {
        max: {
          max: this.max,
          actual: numericValue
        }
      };
    }

    return null;
  }

  onSliderInput(val: any): void {
    const parsed = this.parseValue(val);
    if (parsed === null) return;

    const nextValue = this.clampValue(parsed);
    this.setValue(nextValue, true);
  }

  onNumberInput(val: any): void {
    this.rawInput = (val ?? '').toString();

    if (this.rawInput.trim() === '') {
      this.value = null;
      this.valueChange.emit(null);
      this.onChange(null);
      return;
    }

    const parsed = this.parseValue(this.rawInput);
    if (parsed === null) return;

    const nextValue = this.clampValue(parsed);
    this.setValue(nextValue, true);
  }

  onBlur(): void {
    if (this.value !== null) {
      this.rawInput = this.value.toString();
    } else {
      this.rawInput = '';
    }

    this.onTouched();
  }

  private setValue(val: number | null, emit: boolean = true): void {
    this.value = val;
    this.rawInput = val !== null ? val.toString() : '';

    if (emit) {
      this.valueChange.emit(val);
      this.onChange(val);
    }
  }

  private clampValue(val: number): number {
    let next = val;

    if (next < this.min) next = this.min;
    if (next > this.max) next = this.max;

    // snap to step relative to min
    if (this.step > 0) {
      const snapped = this.min + Math.round((next - this.min) / this.step) * this.step;
      next = Number(snapped.toFixed(this.getStepPrecision()));
      if (next < this.min) next = this.min;
      if (next > this.max) next = this.max;
    }

    return next;
  }

  private parseValue(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    const cleaned = value.toString().replace(/[^0-9.-]/g, '').trim();

    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
      return null;
    }

    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }

  private getStepPrecision(): number {
    const stepString = this.step.toString();
    if (!stepString.includes('.')) return 0;
    return stepString.split('.')[1].length;
  }
}
